import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _excluded = ["children", "disabled", "enlargeOnSelect", "focus", "height", "hovered", "id", "isBoxSelectorDetailed", "isChecked", "isFirst", "isFlat", "isLast", "isNextBoxChecked", "isPreviousBoxChecked", "isShowingDescriptionOnSelect", "isVertical", "left", "keyboardFocused", "name", "onChange", "onDisabledClick", "onLayout", "onMouseDown", "onMouseUp", "onMouseOver", "onMouseOut", "onPress", "pressed", "shouldWordsBreak", "singleRow", "size", "testID", "UNSAFE_inputProps", "value"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import React from 'react';
import deepmerge from 'deepmerge';
import withProvisionalOverrideFromContext from '@bubble-ui/hoc-with-provisional-override-from-context';
import * as Layers from '@bubble-ui/particle-layers';
import { useDesignVersion } from '@bubble-ui/provider-design-version';
import Text from '@bubble-ui/primitive-text';
import { useDesignContext } from '@klarna/designtokens';
import useStyleSheet from '@bubble-ui/hook-use-stylesheet';
import { defaultDesignTokens } from '../defaultDesignTokens';
var MEDIUM_HEIGHT = 40;
var REGULAR_HEIGHT = 50;
var DEFAULT_HEIGHT = 80;
var verticalHeights = {
  REGULAR_HEIGHT: 60,
  ENLARGED_HEIGHT: 80
};
var getDefaultStyleSheet = _ref => {
  var {
    designContext,
    disabled,
    keyboardFocused,
    isChecked,
    isPreviousBoxChecked,
    isNextBoxChecked,
    isFirst,
    isFlat,
    isLast,
    isVertical,
    left,
    size,
    singleRow,
    height,
    enlargeOnSelect,
    shouldWordsBreak
  } = _ref;
  var isMedium = size === 'medium';
  var isRegular = size === 'regular';
  var hasHeight = height !== undefined;
  var narrow = isRegular || isMedium || height && height <= REGULAR_HEIGHT;
  var borderRadius = designContext.get('box-selector/corner-radius').pixelValue;
  var styleVertical = {
    containerStyle: {
      minHeight: enlargeOnSelect && isChecked ? verticalHeights.ENLARGED_HEIGHT : verticalHeights.REGULAR_HEIGHT,
      borderLeftWidth: 1,
      borderRightWidth: 1,
      display: 'flex',
      alignItems: 'center'
    },
    firstStyle: isFirst ? {
      borderTopLeftRadius: isFlat ? 0 : borderRadius,
      borderTopRightRadius: isFlat ? 0 : borderRadius,
      borderTopWidth: 1
    } : undefined,
    lastStyle: isLast ? {
      borderBottomLeftRadius: isFlat ? 0 : borderRadius,
      borderBottomRightRadius: isFlat ? 0 : borderRadius,
      borderBottomWidth: 1
    } : undefined
  };
  var styleHorizontal = {
    containerStyle: _objectSpread(_objectSpread({
      minHeight: isMedium ? MEDIUM_HEIGHT : isRegular ? REGULAR_HEIGHT : hasHeight ? height : DEFAULT_HEIGHT
    }, hasHeight ? {
      height
    } : {}), {}, {
      borderTopWidth: 1,
      borderBottomWidth: 1,
      display: 'flex',
      flexDirection: 'row'
    }),
    firstStyle: isFirst ? {
      borderBottomLeftRadius: isFlat ? 0 : borderRadius,
      borderTopLeftRadius: isFlat ? 0 : borderRadius,
      borderLeftWidth: 1
    } : undefined,
    lastStyle: isLast ? {
      borderTopRightRadius: isFlat ? 0 : borderRadius,
      borderBottomRightRadius: isFlat ? 0 : borderRadius,
      borderRightWidth: 1
    } : undefined
  };
  var transparentBorderColor = designContext.get('colors/borders/transparent').toString();
  var borderColor = designContext.get('box-selector/border-color', {
    selectedState: 'default'
  }).toString();
  var style = {
    containerStyle: {
      boxSizing: 'border-box',
      cursor: disabled ? 'auto' : 'pointer',
      display: 'block',
      position: 'relative',
      minWidth: narrow ? 25 : 0,
      width: '100%',
      borderStyle: 'solid',
      borderRadius: 0,
      borderTopColor: !isFirst && isVertical && (isChecked || isPreviousBoxChecked) ? transparentBorderColor : borderColor,
      borderBottomColor: !isLast && isVertical && (isChecked || isNextBoxChecked) ? transparentBorderColor : borderColor,
      borderRightColor: !isLast && !isVertical && (isChecked || isNextBoxChecked) ? transparentBorderColor : borderColor,
      borderLeftColor: !isFirst && !isVertical && (isChecked || isPreviousBoxChecked) ? transparentBorderColor : borderColor,
      borderTopWidth: 1,
      borderLeftWidth: 1,
      borderBottomWidth: 1,
      borderRightWidth: 1,
      marginLeft: isVertical ? 0 : isFirst ? 0 : -1,
      marginTop: !isVertical ? 0 : isFirst ? 0 : -1,
      backgroundColor: designContext.get('box-selector/background-color', {
        interactiveState: disabled ? 'disabled' : 'default'
      }).toString()
    },
    selectedHighlightStyle: {
      borderColor: designContext.get('box-selector/border-color', {
        selectedState: 'selected'
      }).toString(),
      borderRadius: isFlat ? 0 : borderRadius,
      borderStyle: 'solid',
      borderWidth: 3,
      bottom: -1,
      boxSizing: 'border-box',
      display: 'block',
      left: isVertical ? -1 : isFirst ? -1 : -2,
      opacity: isChecked ? 1 : 0,
      pointerEvents: 'none',
      position: 'absolute',
      right: -1,
      top: !isVertical ? -1 : isFirst ? -1 : -2,
      transition: 'opacity .2s ease',
      zIndex: 5
    },
    focusedHighlightStyle: {
      borderColor: designContext.get('box-selector/border-color', {
        selectedState: 'default'
      }).toString(),
      borderRadius: isFlat ? 0 : borderRadius,
      borderStyle: 'solid',
      borderWidth: 5,
      bottom: -1,
      boxSizing: 'border-box',
      display: 'block',
      left: isVertical ? -1 : isFirst ? -1 : -2,
      opacity: keyboardFocused ? 1 : 0,
      pointerEvents: 'none',
      position: 'absolute',
      right: -1,
      top: !isVertical ? -1 : isFirst ? -1 : -2,
      transition: 'opacity .2s ease',
      zIndex: Layers.FOCUS
    },
    inputStyle: {
      height: '100%',
      opacity: 0,
      position: 'absolute',
      width: '100%',
      top: 0,
      left: 0,
      zIndex: 1,
      cursor: disabled ? 'auto' : 'pointer',
      WebkitTapHighlightColor: designContext.get('colors/backgrounds/transparent').toString()
    },
    textStyle: _objectSpread({
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: singleRow ? 'row' : 'column',
      alignItems: singleRow ? 'center' : 'normal',
      justifyContent: 'center',
      fontSmoothing: 'antialiased',
      left: left ? undefined : '50%',
      paddingLeft: narrow ? 0 : 14,
      paddingRight: narrow ? 0 : 14,
      paddingTop: narrow ? 0 : 9,
      paddingBottom: narrow ? 0 : 9,
      textRendering: 'geometricPrecision',
      transition: 'color .2s ease',
      WebkitFontSmoothing: 'antialiased',
      width: '100%'
    }, shouldWordsBreak ? {
      wordBreak: 'break-word'
    } : {})
  };
  var directionStyle = isVertical ? styleVertical : styleHorizontal;

  // @ts-expect-error
  return deepmerge(style, directionStyle);
};
var BoxSelectorOptionBaseComponent = _ref2 => {
  var {
      children,
      disabled,
      enlargeOnSelect,
      focus,
      height,
      hovered,
      id,
      isBoxSelectorDetailed,
      isChecked,
      isFirst,
      isFlat,
      isLast,
      isNextBoxChecked,
      isPreviousBoxChecked,
      isShowingDescriptionOnSelect,
      isVertical,
      left,
      keyboardFocused,
      name,
      onChange,
      onDisabledClick,
      onLayout,
      onMouseDown,
      onMouseUp,
      onMouseOver,
      onMouseOut,
      onPress,
      pressed,
      shouldWordsBreak,
      singleRow,
      size,
      testID,
      UNSAFE_inputProps,
      value
      /**
       * @deprecated props spreading is outdated and will be removed with v7.0.0
       */
    } = _ref2,
    props = _objectWithoutProperties(_ref2, _excluded);
  var ids = {
    container: "".concat(name, "-").concat(value, "__container"),
    text: "".concat(name, "-").concat(value, "__text"),
    selectedHighlight: "".concat(name, "-").concat(value, "__selected-highlight"),
    focusedHighlight: "".concat(name, "-").concat(value, "__focused-highlight")
  };
  var designVersion = useDesignVersion();
  var designContext = useDesignContext(defaultDesignTokens);
  var {
    containerStyle,
    firstStyle,
    lastStyle,
    inputStyle,
    textStyle,
    selectedHighlightStyle,
    focusedHighlightStyle
    // ⚠️ THIS COMPONENT SUPPORTS STYLE SHEET OVERRIDES ⚠️
    // Keep passing all props to the style sheet, even if we don't use them ourselves
  } = useStyleSheet(BoxSelectorOptionBaseComponent, getDefaultStyleSheet, _objectSpread(_objectSpread({}, props), {}, {
    children,
    designContext,
    designVersion,
    disabled,
    enlargeOnSelect,
    focus,
    height,
    hovered,
    id,
    isBoxSelectorDetailed,
    isChecked,
    isFirst,
    isFlat,
    isLast,
    isNextBoxChecked,
    isPreviousBoxChecked,
    isShowingDescriptionOnSelect,
    isVertical,
    left,
    keyboardFocused,
    name,
    onChange,
    onDisabledClick,
    onLayout,
    onMouseDown,
    onMouseUp,
    onMouseOver,
    onMouseOut,
    onPress,
    pressed,
    shouldWordsBreak,
    singleRow,
    size,
    testID,
    UNSAFE_inputProps,
    value
  }));
  return /*#__PURE__*/React.createElement("label", {
    htmlFor: "".concat(name, "-").concat(value),
    id: ids.container,
    onMouseDown: onMouseDown,
    onMouseUp: onMouseUp,
    onMouseOver: onMouseOver,
    onMouseOut: onMouseOut,
    onClick: e => disabled && onDisabledClick && onDisabledClick(e),
    style: _objectSpread(_objectSpread(_objectSpread({}, containerStyle), firstStyle), lastStyle)
  }, /*#__PURE__*/React.createElement("input", _extends({
    id: "".concat(name, "-").concat(value)
  }, UNSAFE_inputProps, {
    type: "radio",
    checked: isChecked,
    disabled: disabled,
    height: height,
    onChange: e => onChange && !isChecked && onChange(value, e),
    value: value,
    style: inputStyle,
    name: name
  }, props)), /*#__PURE__*/React.createElement(Text, {
    id: ids.text,
    style: textStyle
  }, children), /*#__PURE__*/React.createElement("span", {
    id: ids.focusedHighlight,
    style: focusedHighlightStyle
  }), /*#__PURE__*/React.createElement("span", {
    id: ids.selectedHighlight,
    style: selectedHighlightStyle
  }));
};
BoxSelectorOptionBaseComponent.displayName = 'BoxSelectorOptionBase';
export var BoxSelectorOptionBase = withProvisionalOverrideFromContext(BoxSelectorOptionBaseComponent);
//# sourceMappingURL=BoxSelectorOptionBase.js.map